import { wencesApi } from "@/api/wencesApi";
import { formatDateSync } from "@/utils/dateFormatter";
import { formatNumberSync } from "@/utils/numberFormatter";
import { getTranslatedListLabel } from "@/utils/listItemI18n";
import { buildItemDisplayName } from "@/utils/currentItemLabel";

/**
 * Definiciones de modelo para etiquetar columnas de los widgets. Una sola petición
 * por carga del dashboard aunque haya varios widgets de reporte (el getAll ya tiene
 * su caché en localStorage, ver utils/modelDefinitionCache).
 */
let modelDefsPromise = null;
export function getModelDefinitions() {
  if (!modelDefsPromise) {
    modelDefsPromise = wencesApi
      .get("/api/gotrading/model-definitions")
      .then(({ data }) => (Array.isArray(data) ? data : []))
      .catch(() => {
        modelDefsPromise = null;
        return [];
      });
  }
  return modelDefsPromise;
}

export function findModelDefinition(defs, modelName) {
  const target = String(modelName || "").toLowerCase();
  return (defs || []).find((d) => String(d.modelName || "").toLowerCase() === target) || null;
}

/** Campo de una ruta con puntos: `client`, `expenses.amount` (group), `proform.user` (referencia). */
export function resolveFieldPath(defs, modelName, path) {
  const parts = String(path || "").split(".");
  let fields = (findModelDefinition(defs, modelName) || {}).fields || [];
  let field = null;
  const labels = [];
  for (const part of parts) {
    field = fields.find((f) => f && f.key === part) || null;
    if (!field) return { key: path, label: labels.concat(part).join(" · "), type: "string" };
    labels.push(field.abbreviation || field.label || field.key);
    if (field.type === "group") fields = field.fields || [];
    else if (field.type === "reference" && field.ref) fields = (findModelDefinition(defs, field.ref) || {}).fields || [];
    else fields = [];
  }
  return { ...field, key: path, label: labels.join(" · ") };
}

/** Valor de una ruta con puntos; si pasa por un arreglo (group), junta los de cada fila. */
export function getPathValue(item, path) {
  const parts = String(path || "").split(".");
  let current = [item];
  for (const part of parts) {
    const next = [];
    current.forEach((v) => {
      if (v == null) return;
      const child = Array.isArray(v) ? v.map((x) => (x == null ? x : x[part])) : v[part];
      if (Array.isArray(child)) next.push(...child);
      else next.push(child);
    });
    current = next;
  }
  const values = current.filter((v) => v !== undefined && v !== null && v !== "");
  if (values.length === 0) return null;
  return values.length === 1 ? values[0] : values;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T|$)/;

/** Texto corto para mostrar un valor en un widget. */
export function displayValue(value, field, locale) {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) {
    const parts = value.map((v) => displayValue(v, field, locale)).filter(Boolean);
    const unique = Array.from(new Set(parts));
    return unique.length > 3 ? `${unique.slice(0, 3).join(", ")} +${unique.length - 3}` : unique.join(", ");
  }
  const type = field && field.type;
  if (typeof value === "boolean") return value ? "✓" : "✗";
  if (typeof value === "object") {
    const listLabel = getTranslatedListLabel(value, locale);
    if (listLabel) return listLabel;
    return buildItemDisplayName(value);
  }
  if (type === "date" || (typeof value === "string" && ISO_DATE_RE.test(value))) {
    return formatDateSync(value, String(value));
  }
  if (typeof value === "number" || type === "number") {
    const formatted = formatNumberSync(value);
    return formatted === "" ? String(value) : formatted;
  }
  return String(value);
}

export function toNumber(value) {
  if (Array.isArray(value)) return value.reduce((sum, v) => sum + toNumber(v), 0);
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) return Number(value);
  return 0;
}

export function formatCompactNumber(value) {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${(n / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}M`;
  if (abs >= 1e4) return `${(n / 1e3).toFixed(0)}k`;
  return formatNumberSync(n) || String(n);
}

export const CHART_TYPES = ["column", "bar", "line", "area", "pie", "donut"];
export const DATE_BUCKETS = ["day", "week", "month", "year"];
export const AGGREGATIONS = ["count", "sum", "avg", "max", "min"];

const pad2 = (n) => String(n).padStart(2, "0");

/** Clave ordenable y etiqueta corta de una fecha según el agrupamiento. */
function dateBucket(value, bucket, locale) {
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  if (bucket === "year") return { key: String(y), label: String(y) };
  if (bucket === "day") {
    const key = `${y}-${pad2(m + 1)}-${pad2(d.getUTCDate())}`;
    return { key, label: `${pad2(d.getUTCDate())}/${pad2(m + 1)}` };
  }
  if (bucket === "week") {
    // Semana que empieza el lunes.
    const day = (d.getUTCDay() + 6) % 7;
    const start = new Date(Date.UTC(y, m, d.getUTCDate() - day));
    const key = start.toISOString().slice(0, 10);
    return { key, label: `${pad2(start.getUTCDate())}/${pad2(start.getUTCMonth() + 1)}` };
  }
  const label = new Intl.DateTimeFormat(locale || "es", { month: "short", year: "2-digit", timeZone: "UTC" })
    .format(d)
    .replace(".", "");
  return { key: `${y}-${pad2(m + 1)}`, label };
}

/**
 * Agrupa filas para un gráfico de una serie: eje X = un campo (o una fecha agrupada),
 * eje Y = cantidad de filas o suma/promedio/máx/mín de un campo numérico.
 * Devuelve `[{ label, value, other? }]`.
 */
export function aggregatePoints(items, opts) {
  const { xField, xDate = "month", agg = "count", yField = null, limit = 8, sort = "value", locale, emptyLabel = "—", otherLabel = "Otros" } = opts;
  if (!xField) return [];
  const isDate = xField.type === "date";
  const groups = new Map();
  items.forEach((item) => {
    const raw = getPathValue(item, xField.key);
    let keys;
    if (isDate) {
      const b = dateBucket(raw, xDate, locale);
      keys = b ? [b] : [];
    } else {
      const values = Array.isArray(raw) ? raw : [raw];
      const labels = Array.from(new Set(values.map((v) => displayValue(v, xField, locale) || emptyLabel)));
      keys = labels.map((l) => ({ key: l, label: l }));
    }
    const y = agg === "count" || !yField ? 1 : toNumber(getPathValue(item, yField.key));
    keys.forEach(({ key, label }) => {
      const g = groups.get(key) || { key, label, n: 0, sum: 0, max: -Infinity, min: Infinity };
      g.n += 1;
      g.sum += y;
      g.max = Math.max(g.max, y);
      g.min = Math.min(g.min, y);
      groups.set(key, g);
    });
  });
  const valueOf = (g) => {
    if (agg === "count" || !yField) return g.n;
    if (agg === "avg") return g.n ? Math.round((g.sum / g.n) * 100) / 100 : 0;
    if (agg === "max") return g.max === -Infinity ? 0 : g.max;
    if (agg === "min") return g.min === Infinity ? 0 : g.min;
    return Math.round(g.sum * 100) / 100;
  };
  let rows = Array.from(groups.values()).map((g) => ({ key: g.key, label: g.label, value: valueOf(g), n: g.n, sum: g.sum }));
  if (isDate) {
    // Una fecha se lee en orden: se muestran los últimos `limit` períodos.
    rows.sort((a, b) => (a.key < b.key ? -1 : 1));
    return rows.slice(-limit).map(({ label, value }) => ({ label, value }));
  }
  if (sort === "label") {
    rows.sort((a, b) => String(a.label).localeCompare(String(b.label), locale || "es"));
    return rows.slice(0, limit).map(({ label, value }) => ({ label, value }));
  }
  rows.sort((a, b) => b.value - a.value);
  if (rows.length > limit && agg !== "avg" && agg !== "max" && agg !== "min") {
    // El resto se junta en «Otros» (solo tiene sentido para cantidades y sumas).
    const rest = rows.slice(limit - 1);
    const other = { label: otherLabel, value: rest.reduce((s, r) => s + r.value, 0), other: true };
    rows = rows.slice(0, limit - 1).concat(other);
  } else {
    rows = rows.slice(0, limit);
  }
  return rows.map(({ label, value, other }) => (other ? { label, value, other } : { label, value }));
}
